import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/dafbp3htto0ceqqnh9mg';

const TOPICS = ['投稿爆料 / 线索提供', '专题选题建议', '媒体报道与合作', '意见与反馈', '其他'];

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // honeypot anti-spam
    const honeypot = String(fd.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('感谢你的来信，我们已经收到并会尽快处理。');
      form.reset();
      return;
    }
    fd.delete('website_alt');

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      payload.append(key, typeof value === 'string' ? value : 'Uncollectable');
    });

    setStatus('idle');
    setMessage('');
    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = { message: responseText };
      }
      const bodyLow = (responseText || '').toLowerCase();
      const serverMsg =
        (parsed as { meta?: { message?: string } }).meta?.message ||
        parsed.message ||
        responseText;

      if (
        res.ok &&
        parsed.code === 'OK' &&
        !bodyLow.includes('spam') &&
        !serverMsg.toLowerCase().includes('spam')
      ) {
        setStatus('success');
        setMessage('投稿已收到，感谢你的线索！编辑部会尽快审阅。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络开小差了，请检查后再提交。');
    }
  }

  const infoItems = [
    { icon: 'ri-mail-send-line', label: '投稿与编辑部', value: 'tips@chigua-wangbao.example.com' },
    { icon: 'ri-briefcase-line', label: '商务与媒体合作', value: 'bd@chigua-wangbao.example.com' },
    { icon: 'ri-feedback-line', label: '内容更正与反馈', value: '反馈会在 1-2 个工作日内处理' },
  ];

  return (
    <section id="contact" className="mx-auto w-full max-w-7xl scroll-mt-24 px-4 py-12 md:px-6 md:py-16">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-10">
        {/* info */}
        <div className="lg:col-span-2">
          <p className="flex items-center gap-1.5 text-xs font-semibold tracking-widest text-primary-600">
            <i className="ri-send-plane-line"></i> 投稿爆料
          </p>
          <h2 className="heading-font mt-2 text-2xl font-extrabold leading-snug text-foreground-950 md:text-3xl">
            发现了好瓜？
            <br />
            把它交给我们
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            你有线索、观点或想讨论的方向，都可以通过表单告诉编辑部。素材足够扎实的，会被整理成热点或专题，被更多人看到。
          </p>

          <ul className="mt-6 space-y-3">
            {infoItems.map((it) => (
              <li key={it.label} className="flex items-start gap-3 rounded-lg border border-secondary-100 bg-background-50 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className={`${it.icon} text-lg`}></i>
                </span>
                <span>
                  <span className="block text-[11px] text-foreground-500">{it.label}</span>
                  <span className="block text-sm font-semibold text-foreground-800">{it.value}</span>
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex items-center gap-2 rounded-lg bg-accent-100/70 p-4 text-xs leading-relaxed text-accent-900">
            <i className="ri-shield-check-line shrink-0 text-lg"></i>
            线索来源会做脱敏处理，未经允许不会公开你的个人信息。理性吃瓜，我们共同维护讨论氛围。
          </div>
        </div>

        {/* form */}
        <div className="rounded-lg border border-secondary-100 bg-background-50 p-5 md:p-7 lg:col-span-3">
          <h3 className="heading-font text-lg font-bold text-foreground-950">给编辑部留言</h3>
          <form
            onSubmit={handleSubmit}
            data-readdy-form="吃瓜网爆爆料投稿联系表单"
            className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            <div>
              <label htmlFor="name" className="mb-1.5 block text-xs font-medium text-foreground-700">
                怎么称呼你
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                maxLength={30}
                placeholder="你的昵称"
                className="w-full rounded-md border border-secondary-200 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div>
              <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-foreground-700">
                联系邮箱
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                maxLength={60}
                placeholder="you@example.com"
                className="w-full rounded-md border border-secondary-200 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="topic" className="mb-1.5 block text-xs font-medium text-foreground-700">
                事情方向
              </label>
              <select
                id="topic"
                name="topic"
                required
                defaultValue={TOPICS[0]}
                className="w-full rounded-md border border-secondary-200 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                {TOPICS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="message" className="mb-1.5 block text-xs font-medium text-foreground-700">
                具体内容 / 线索描述
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请尽量描述清楚事件时间、相关方与你想分享的内容……（限 500 字）"
                className="w-full resize-none rounded-md border border-secondary-200 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              ></textarea>
            </div>

            {/* honeypot */}
            <div className="field-moat" aria-hidden="true">
              <label htmlFor="website_alt">website</label>
              <input
                id="website_alt"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
                value=""
                aria-hidden="true"
              />
            </div>

            <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[11px] text-foreground-400">提交即代表同意编辑部对线索进行核实用途。</p>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-fill"></i> 提交留言
              </button>
            </div>

            {status === 'success' && (
              <div className="sm:col-span-2 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                <i className="ri-checkbox-circle-fill"></i> {message}
              </div>
            )}
            {status === 'error' && (
              <div className="sm:col-span-2 flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
                <i className="ri-error-warning-fill"></i> {message}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}